import { Fragment } from "react";
import { calcSelectedOptionIds } from "src/pages/configurator/calcSelectedOptionIds";
import { UserWarning } from "src/pages/configurator/components";
import { ImageDisplay, ImageDisplayImageProps } from "src/pages/configurator/components/ImageDisplay";
import { OptionListContainer, OptionListItem } from "src/pages/configurator/components/options";
import { useConfiguratorContext } from "src/pages/configurator/Configurator.context";
import { useComputed } from "src/utils/hooks/useComputed";
import { AccordionOptionCard } from "~components";
import { CheckoutConfigOptionGroupStep } from "~generated/graphqlTypes";
import { useTestIds } from "~utils";
import { ConfiguratorStepper } from "../components/stepper/stepper";
import { StepWrapper } from "../components/StepWrapper";

export function ExteriorStep() {
  const { toggleOption, formState, checkoutConfig, showPriceLoadingState, autoSaving, selectedOptionIds } =
    useConfiguratorContext();
  const tid = useTestIds({}, "exterior-step");

  const [isReversed, loading] = useComputed(() => [formState.isReversed.value, formState.loading], [formState]);

  // Get all exterior-related option groups in a flat list
  const exteriorOptionGroups =
    checkoutConfig?.optionGroups
      .filter(
        (ccgo) =>
          ccgo.step === CheckoutConfigOptionGroupStep.ExteriorElevation ||
          ccgo.step === CheckoutConfigOptionGroupStep.ExteriorDesignPackage,
      )
      // If we're looking at exterior scheme, and we "truly" only have one exterior scheme option available regardless of active or not,
      // then don't even bother showing this group
      .filter((ccog) => {
        if (ccog.group.group.type.isExteriorScheme) {
          return ccog.options.length > 1;
        }
        return true;
      }) ?? [];
  const isDisabled = !checkoutConfig?.canEdit.allowed;

  // We will need to check both loading and autoSaving to show the loading state (since we auto save when changing options)
  const isLoading = loading || autoSaving;

  const images: ImageDisplayImageProps[] = checkoutConfig?.rendering
    ? [
        {
          kind: "image",
          isRendering: true,
          id: checkoutConfig.rendering.id,
          imageUrl: checkoutConfig.rendering.largePreview!,
          isReversed: !!isReversed,
        },
      ]
    : [];

  return (
    <>
      <StepWrapper
        loading={isLoading}
        loadingView="accordion"
        title="Exterior"
        description="Select your home's architecture style and exterior color scheme."
        warningNode={
          !isLoading &&
          isDisabled && (
            <UserWarning
              title="Exterior options can no longer be changed."
              disabledReasons={[checkoutConfig?.canEdit]}
            />
          )
        }
        left={<ImageDisplay images={images} isReversed={isReversed ?? false} {...tid.imageDisplay} />}
        right={
          <>
            {exteriorOptionGroups.map((ccog) => {
              return (
                <AccordionOptionCard
                  key={ccog.group.id}
                  title={ccog.group.name}
                  cutoffDate={ccog.cutoffDate}
                  children={
                    <OptionListContainer>
                      {ccog.options
                        // Only show allowed ext schemes for the selected elevation
                        .filter((cco) => cco.canBeSelected.allowed)
                        .map((cco) => {
                          const { id, name, description } = cco.option;

                          const { removedOptions } = calcSelectedOptionIds(
                            checkoutConfig?.optionGroups ?? [],
                            selectedOptionIds,
                            ccog,
                            cco,
                          );

                          return (
                            <Fragment key={id}>
                              <OptionListItem
                                isRadio
                                autoSaving={showPriceLoadingState(id)}
                                optionId={id}
                                conflicts={removedOptions}
                                title={name}
                                description={description}
                                selected={selectedOptionIds.includes(id)}
                                onClick={() => toggleOption(ccog, cco)}
                                priceInCents={cco.totalPriceInCents}
                                disabled={isDisabled || autoSaving}
                                {...tid.optionListItem}
                              />
                            </Fragment>
                          );
                        })}
                    </OptionListContainer>
                  }
                  {...tid.accordionOptionCard}
                />
              );
            })}
          </>
        }
      />
      <ConfiguratorStepper />
    </>
  );
}
